import React, { useEffect, useState } from "react";
import DetailsPanelSkeleton from "./DetailsPanelSkeleton";
import appFetch from "../../appFetch";
import {
  appendQueryToUri,
  buildEditData,
  decodeHTML,
  encodeHTML,
  getSize,
} from "../../util/generalUtil";
import apiEndpoints from "../../apiEndpoints";
import AttachmentPreviewModal from "../AttachmentPreviewModal";
import TransactionLine from "../TransactionLine";
import { CheckIcon, ChevronLeft, Edit3Icon, XIcon } from "lucide-react";
import ActionButtons from "./ActionButtons";
import classNames from "classnames";
import StandaloneRichText from "../StandaloneRichText";
import OutlookReplyButton from "../OutlookReplyButton";
import { MailIcon } from "@heroicons/react/solid";
import WithLoader from "../WithLoader";
import moment from "moment";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import useMenuUpdate from "../../hooks/useMenuUpdate";
import { editorConfig } from "../ExtendedRichText";
import "../../styles/JoditScrollbar.css";
import PdfIcon from "../../icons/PdfIcon";
import ExcelIcon from "../../icons/ExcelIcon";
import WordDocumentIcon from "../../icons/WordDocumentIcon";
import icons from "../../icons";
import MicrosoftWordIcon from "../../icons/MicrosoftWordIcon";
import Spinner from "../../loaders/Spinner";
import { Formik, Form as FormikForm, Field, useFormikContext } from "formik";
import { Dialog } from "@headlessui/react";

const MySwal = withReactContent(
  Swal.mixin({
    customClass: {
      confirmButton:
        "text-white font-medium rounded-lg text-sm px-5 py-2.5 text-center mr-2 mb-2 bg-primary",
      cancelButton:
        "text-white font-medium rounded-lg text-sm px-5 py-2.5 text-center mr-2 mb-2 bg-primary",
    },
    buttonsStyling: false,
  })
);

function convertToReadableString(raw) {
  if (raw === undefined || raw === null) return "";

  let s = String(raw);

  // Replace ? or HTML entity or already existing ₦ before digits with the Naira sign
  s = s.replace(/(\?|&#8358;|₦)(?=\d)/g, "₦");

  // Remove commas that occur just before a lowercase-word clause (e.g. "119,700, who" -> "119,700 who")
  s = s.replace(/,(\s+[a-z])/g, "$1");

  // Collapse multiple spaces
  s = s.replace(/\s{2,}/g, " ");

  return s.trim();
}

const CommentModal = ({
  isOpen,
  setIsOpen,
  buttonData,
  updateData,
  processing,
  setProcessing,
}) => {
  let { data, buttonAction, button, buttonName } = buttonData;
  return (
    <Dialog
      open={isOpen}
      onClose={() => {}}
      className="fixed z-10 inset-0 overflow-y-auto"
    >
      <div className="flex items-center justify-center min-h-screen h-full">
        <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />
        <div className="relative bg-white mx-auto my-10 rounded-md">
          <div className="p-6 pt-14">
            <button
              className="absolute right-4 top-4"
              onClick={() => {
                setIsOpen(false);
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 21 21"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12.9651 10.4985L20.4829 2.99719C20.8121 2.66793 20.997 2.22136 20.997 1.75571C20.997 1.29007 20.8121 0.843497 20.4829 0.514237C20.1536 0.184977 19.7071 0 19.2416 0C18.776 0 18.3295 0.184977 18.0003 0.514237L10.5 8.03304L2.99975 0.514237C2.67053 0.184977 2.22403 -3.46932e-09 1.75845 0C1.29287 3.46932e-09 0.846361 0.184977 0.517147 0.514237C0.187934 0.843497 0.00298367 1.29007 0.00298367 1.75571C0.00298366 2.22136 0.187934 2.66793 0.517147 2.99719L8.03488 10.4985L0.517147 17.9998C0.353281 18.1624 0.223217 18.3558 0.134458 18.5688C0.0456982 18.7819 0 19.0105 0 19.2413C0 19.4721 0.0456982 19.7007 0.134458 19.9138C0.223217 20.1268 0.353281 20.3202 0.517147 20.4828C0.679675 20.6467 0.87304 20.7768 1.08609 20.8655C1.29914 20.9543 1.52765 21 1.75845 21C1.98925 21 2.21776 20.9543 2.43081 20.8655C2.64386 20.7768 2.83722 20.6467 2.99975 20.4828L10.5 12.964L18.0003 20.4828C18.1628 20.6467 18.3561 20.7768 18.5692 20.8655C18.7822 20.9543 19.0108 21 19.2416 21C19.4723 21 19.7009 20.9543 19.9139 20.8655C20.127 20.7768 20.3203 20.6467 20.4829 20.4828C20.6467 20.3202 20.7768 20.1268 20.8655 19.9138C20.9543 19.7007 21 19.4721 21 19.2413C21 19.0105 20.9543 18.7819 20.8655 18.5688C20.7768 18.3558 20.6467 18.1624 20.4829 17.9998L12.9651 10.4985Z"
                  fill="#747474"
                />
              </svg>
            </button>
            <div className="">
              <Formik
                initialValues={{ APPROVAL_MEMO: "" }}
                onSubmit={async (values, { resetForm }) => {
                  let newData = { ...data };
                  newData["_HEADER"] = { ...newData["_HEADER"], ...values };
                  setProcessing(true);
                  updateData(newData, button);
                }}
              >
                <FormikForm className="grid">
                  <Field
                    as="textarea"
                    rows={6}
                    cols={50}
                    name="APPROVAL_MEMO"
                    autoComplete="off"
                    placeholder="add comment"
                    className="pl-3 placeholder-custom-gray-100 placeholder-opacity-40 appearance-none relative block w-full px-3 py-2 border border-custom-gray-300 text-gray-500 rounded-md focus:outline-none sm:text-sm"
                  />
                  <button className="mt-3 bg-primary text-white py-2 rounded-md inline-block self-end inline-flex justify-center">
                    {processing ? (
                      <Spinner width={20} height={20} fill="#f7f7f7" />
                    ) : (
                      buttonAction
                    )}
                  </button>
                </FormikForm>
              </Formik>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

const DefaultIcon = () => {
  return (
    <div className="flex-shrink-0">
      <svg
        className="w-8 h-8 text-red-600"
        fill="currentColor"
        viewBox="0 0 24 24"
      >
        <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
      </svg>
    </div>
  );
};

const iconMap = {
  pdf: "PdfIcon",
  excel: "ExcelIcon",
  word: "MicrosoftWordIcon",
  none: "DefaultIcon",
};

const DetailsPanel = ({
  expense,
  isFullScreen,
  onToggleFullScreen,
  onClose,
  targetid,
  layoutid,
  setLoading: setPageLoading,
  setOptions: setEmailListOptions,
  menuId,
}) => {
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [options, setOptions] = useState(null);
  const [formContext, setFormContext] = useState(null);
  const [editorContent, setEditorContent] = useState("");
  const { updateMenu } = useMenuUpdate();
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const [buttonData, updateButtonData] = useState(null);
  const [processing, setProcessing] = useState(false);

  // Edit subject state
  const [isEditingSubject, setIsEditingSubject] = useState(false);
  const [editedSubject, setEditedSubject] = useState("");

  const updateSubject = (event) => {
    const value = event.target.value;
    setEditedSubject(value);
  };

  useEffect(() => {
    setLoading(true);
    setTransactionType();
  }, [expense.Drillid]);

  const setTransactionType = (item) => {
    let data = {
      layoutId: layoutid,
      targetId: targetid,
      target: expense?.target?.toUpperCase(),
      targetType: expense?.target_type,
      drillId: expense?.Drillid,
    };

    appFetch
      .get(appendQueryToUri(apiEndpoints.menuView, data))
      .then((result) => {
        const data = result.options[0];
        setOptions(data);

        const editContentDecoded = decodeHTML(
          (data?.edit_content || [])[0]?.value ?? ""
        );
        const editSubjectDecoded = decodeHTML(
          (data?.edit_subject || [])[0]?.value ?? ""
        );

        setEditorContent(editContentDecoded);
        setEditedSubject(editSubjectDecoded);
      })
      .catch((e) => console.log(e.message))
      .finally(() => setLoading(false));
  };

  const handleFormContextReady = (context) => {
    setFormContext(context);
  };

  const handleEditSubject = () => {
    setIsEditingSubject(true);
  };

  const handleSaveSubject = () => {
    // Here you would typically save to your backend
    console.log("Saving subject:", editedSubject);
    // For now, just exit edit mode
    setIsEditingSubject(false);
  };

  const handleCancelEdit = () => {
    setEditedSubject(editedSubject || "");
    setIsEditingSubject(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSaveSubject();
    } else if (e.key === "Escape") {
      handleCancelEdit();
    }
  };

  if (isLoading || !options) {
    return <DetailsPanelSkeleton isFullScreen={isFullScreen} />;
  }

  let {
    line_header,
    line_data,
    attachment,
    line_control_edit,
    edit_header,
    email_header: headers,
    summary,
    target,
    button,
  } = options;

  let lines = line_data ?? [];
  let header = (line_header || [])[0]?.name ?? [];
  let editable_controls = (line_control_edit || [])[0]?.name ?? [];
  let hasUpload = editable_controls?.find((m) => m.type == "upload");
  let hasDownload = editable_controls?.find((m) => m.type == "download");

  let headerControl = edit_header ?? [];
  header = header?.filter((str) => !/attachment/i.test(str));
  editable_controls = editable_controls?.filter(
    (row) => !/attachment/i.test(row?.column)
  );

  // const myAttachments = attachment?.reduce((a, b) =>
  //   a.concat(b?.name?.map(x => x.replace("http","https")))
  // , [])
  // .map((x, idx) => ({name: getFileNameFromUrl(x), url: x, id: idx}));

  const myAttachments = attachment?.reduce(
    (a, b) => a.concat(b?.line_attachment),
    []
  );

  const handleAttachmentClick = (attachment) => {
    setPreviewAttachment(attachment);
  };

  const handleButtonClick = (button) => {
    const postData = {};

    console.log("options", options);

    const editContentModified = { ...(options?.edit_content?.[0] ?? {}) };
    editContentModified[editContentModified.name] = encodeHTML(editorContent);
    const editSubjectModified = { ...options.edit_subject[0] };
    editSubjectModified[editSubjectModified.name] = encodeHTML(editedSubject);

    // console.log('values from details panel', {
    //   button,
    //   newValues,
    //   newEditableControls
    // })

    buildEditData(postData, edit_header[0]);
    buildEditData(postData, editContentModified);
    buildEditData(postData, editSubjectModified);

    lines?.forEach((row) => {
      var data = {};
      for (let control of editable_controls) {
        let { name, type, response } = control;

        if (/label/i.test(type)) {
          continue;
        }

        let ref = data;
        let count = 1;
        let prop = "";
        while (true) {
          prop = control[`root_${count}`];

          //check if next exist
          if (typeof control[`root_${count + 1}`] == "undefined") break;

          ref[prop] = ref[prop] || {};
          ref = ref[prop];

          count++;
        }

        if (!prop) continue;
        let newName = `${name}_line_${row["LINE"]}`;

        let formValue = formContext.values[newName];

        if (type === "number") {
          formValue = formValue?.toString()?.replace(/,/g, "");
        }

        if (Array.isArray(formValue)) {
          if (formValue.length > 1) {
            formValue = formValue.map((m) => m?.value);
          } else {
            formValue = formValue[0]?.value ? [formValue[0].value] : [];
          }
        }

        if ({}.constructor === formValue?.constructor) {
          formValue = formValue?.value;
        }

        if (moment.isDate(formValue)) {
          formValue = moment(formValue).format("YYYY-MM-DD h:mmA");
        }

        formValue =
          response === "array"
            ? [].concat(formValue).filter((m) => m) // Ensure it's an array and filter out falsy values
            : formValue;

        console.log("let me check something", formValue, response);
        ref[prop] = formValue;
      }

      let lineProp = Object.keys(data).slice(-1)[0];

      if (lineProp) {
        data[lineProp]["LINE"] = row["LINE"];
        postData[lineProp] = (postData[lineProp] ?? []).concat(data[lineProp]);
      }
    });

    updateButtonData({
      data: postData,
      buttonAction: button?.action_type ?? "",
      button,
      buttonName: button?.action_type,
    });
    setCommentModalOpen(true);

    // updateData(postData, button);
  };

  const updateData = (data, buttonAction) => {
    let postData = {
      jsonString: JSON.stringify([data]),
      menuId: menuId,
      pageButtonActionId: null,
      buttonAction: buttonAction.value,
    };

    setPageLoading(true);
    appFetch
      .post(apiEndpoints.menuAction, {
        body: JSON.stringify(postData),
      })
      .then((result) => {
        let { ERROR_MESSAGE: errMsg, ERROR_NUMBER: errNo, layout } = result;

        // Navigate to the groups node
        const groups =
          layout.subcomponents[1].subcomponents[0].subcomponents[0].options[0]
            .groups;

        MySwal.fire({
          text: errMsg,
          icon: errNo === 0 ? "success" : "error",
        }).then(() => {
          if (["approve", "reject"].includes(buttonAction.name.toLowerCase())) {
            onClose(groups);
          }
        });

        if (errNo !== 0) {
          throw new Error(errMsg);
        }

        let emailListOptions =
          result?.layout.subcomponents[1]?.subcomponents[0]?.subcomponents[0]
            ?.options;
        console.log("emailListOptions", { emailListOptions, btn: button.name });

        updateMenu(result?.MENU_BADGE);

        setEmailListOptions(emailListOptions);
      })
      .catch((e) => console.log(e.message))
      .finally(() => {
        setCommentModalOpen(false);
        setProcessing(false);
        setPageLoading(false);
      });
  };

  if (!expense) return null;

  return (
    <>
      {commentModalOpen && (
        <CommentModal
          buttonData={buttonData}
          setIsOpen={setCommentModalOpen}
          isOpen={commentModalOpen}
          updateData={updateData}
          processing={processing}
          setProcessing={setProcessing}
        />
      )}

      <div
        className={`bg-white border-l border-gray-200 h-screen ${
          isFullScreen ? "fixed inset-0 z-40" : "w-1/2"
        } flex flex-col`}
      >
        {/* Header */}
        <div className="p-6">
          <div className="flex justify-between items-center mb-2">
            <div className="flex items-center gap-2">
              <button
                onClick={() => onClose()}
                className="border p-2 border-gray-300 rounded-md 300 p-2 text-gray-400 hover:text-gray-600 transition-colors"
                title="Close"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <OutlookReplyButton
                to={options?.edit_reply}
                subject={convertToReadableString(editedSubject)}
                body={editorContent}
              >
                <span className="flex items-center gap-2 text-xs">
                  <MailIcon className="cw-6 h-6" />
                  Reply
                </span>
              </OutlookReplyButton>
            </div>
            <div className="flex items-center bg-gray-100 rounded p-1">
              {/* Split screen button */}
              <button
                onClick={onToggleFullScreen}
                className={`p-1 rounded transition-colors ${
                  !isFullScreen
                    ? "bg-primary text-white"
                    : "text-gray-400 hover:text-gray-600"
                }`}
                title="Split screen"
              >
                <svg
                  className="w-10 h-6"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <rect x="2" y="5" width="8" height="14" rx="1" />
                  <rect x="14" y="5" width="8" height="14" rx="1" />
                </svg>
              </button>

              {/* Fullscreen button */}
              <button
                onClick={onToggleFullScreen}
                className={`p-1 rounded transition-colors ${
                  isFullScreen
                    ? "bg-primary text-white"
                    : "text-gray-400 hover:text-gray-600"
                }`}
                title="Fullscreen"
              >
                <svg
                  className="w-10 h-6"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <rect x="3" y="5" width="18" height="14" rx="1" />
                </svg>
              </button>
            </div>
          </div>

          <div className="border border-gray-300 rounded-md">
            <div className="bg-gray-100 flex items-center justify-between px-6 py-2 overflow-hidden rounded-md">
              <div
                className={classNames("flex items-center gap-2 min-w-0 mr-4", {
                  "flex-1": isEditingSubject,
                })}
              >
                {isEditingSubject ? (
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <input
                      type="text"
                      value={convertToReadableString(editedSubject)}
                      onChange={updateSubject}
                      onKeyDown={handleKeyDown}
                      className="flex-1 text-lg font-semibold text-gray-900 bg-white border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-0"
                      autoFocus
                    />
                    <button
                      onClick={handleSaveSubject}
                      className="text-green-600 hover:text-green-700 transition-colors p-1"
                      title="Save"
                    >
                      <CheckIcon className="w-5 h-5" />
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                      title="Cancel"
                    >
                      <XIcon className="w-5 h-5" />
                    </button>
                  </div>
                ) : (
                  <>
                    <h2 className="text-lg font-semibold text-gray-900 truncate flex-1 min-w-0">
                      {convertToReadableString(editedSubject)}
                    </h2>
                    <button
                      onClick={handleEditSubject}
                      className="text-primary hover:text-primary/80 transition-colors flex-shrink-0"
                      title="Edit subject"
                    >
                      <Edit3Icon className="w-5 h-5" />
                    </button>
                  </>
                )}
              </div>

              {formContext && !isEditingSubject && (
                <div className="flex gap-3 flex-shrink-0">
                  <ActionButtons
                    buttons={button}
                    onButtonClick={handleButtonClick}
                  />
                </div>
              )}
            </div>

            {/* Content with Custom Scrollbar */}
            <div
              className={`flex-1 overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400 scrollbar-thumb-rounded-full scrollbar-track-rounded-full max-h-[calc(100vh-100px)]`}
            >
              <div className="space-y-1">
                {headers?.map(({ name, value }) => (
                  <div className="grid grid-cols-2 gap-4" key={name}>
                    <span className="text-sm font-medium text-gray-500">
                      {name}
                    </span>
                    <p className="text-sm text-gray-900">{value}</p>
                  </div>
                ))}
              </div>

              {/* Attachments */}
              <div className="my-2">
                <div className="flex gap-4 flex-wrap">
                  {myAttachments?.map((attachment, idx) => {
                    if (!attachment || !attachment.name) {
                      return null;
                    }

                    let menuIcon = iconMap["excel"];

                    // console.log("attachments", myAttachments);
                    if (attachment?.name?.includes(".pdf")) {
                      menuIcon = iconMap["pdf"];
                    } else if (attachment?.name?.includes(".docx")) {
                      menuIcon = iconMap["word"];
                    } else if (attachment?.name?.includes(".xlsx")) {
                      menuIcon = iconMap["excel"];
                    } else {
                      menuIcon = null;
                    }

                    console.log("the menuicon  is", menuIcon, attachment);

                    return (
                      <div
                        key={idx}
                        onClick={() => handleAttachmentClick(attachment)}
                        className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        {icons[menuIcon] ? (
                          React.createElement(icons[menuIcon], {
                            width: 20,
                            height: null,
                          })
                        ) : (
                          <div className="flex-shrink-0">
                            <svg
                              className="w-8 h-8 text-red-600"
                              fill="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                            </svg>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-gray-900 truncate">
                            {attachment.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {getSize(attachment.size)}
                          </p>
                        </div>
                        <div className="flex-shrink-0">
                          <svg
                            className="w-4 h-4 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                            />
                          </svg>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <hr className="border-t border-gray-300 my-1" />

              {/* Expense Details */}
              <div className="space-y-1">
                {summary?.map(({ name, value }) => (
                  <div className="grid grid-cols-2 gap-4" key={name}>
                    <span className="text-sm font-medium text-gray-500">
                      {name}
                    </span>
                    {name === "Amount" ? (
                      <p
                        className="text-sm font-medium text-gray-900"
                        dangerouslySetInnerHTML={{ __html: value }}
                      ></p>
                    ) : (
                      <p className="text-sm text-gray-900">{value}</p>
                    )}
                  </div>
                ))}
              </div>

              {/* TransactionLine */}
              <div className="my-4 border border-gray-100">
                <TransactionLine
                  // Data props
                  header={header}
                  lines={lines}
                  editable_controls={editable_controls}
                  // Configuration props
                  hasUpload={hasUpload}
                  hasDownload={hasDownload}
                  // Form submission props
                  headerControl={headerControl}
                  item={options}
                  // Layout props
                  layoutid={layoutid}
                  target={target}
                  targetid={targetid}
                  // Form context callback
                  onFormContextReady={handleFormContextReady}

                  // Additional props
                  // action={action}
                />
              </div>

              {/* Memo Section */}
              <div className="mb-8">
                <h3 className="text-sm font-medium text-gray-900 mb-3">
                  Narration
                </h3>
                <div className="border border-gray-200 rounded-lg">
                  {/* <ExtendedRichText /> */}
                  <StandaloneRichText
                    config={editorConfig}
                    name="description"
                    label="Description"
                    value={editorContent}
                    onChange={setEditorContent}
                    validation={{ required: true }}
                    indent="yes"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AttachmentPreviewModal
        isOpen={!!previewAttachment}
        onClose={() => setPreviewAttachment(null)}
        attachment={previewAttachment}
      />
    </>
  );
};

export default WithLoader(DetailsPanel);
